import { supabase } from '../../lib/supabase';
import type {
  ActivityItem,
  DutyGroup,
  AgentSettingsRow,
  ApprovalRuleRow,
  ArtifactRow,
  BotRow,
  ChatRow,
  Duties,
  SalesDuties,
  LabEvent,
  MemoryKind,
  MemoryRow,
  MessageRow,
  NotifyLevel,
  RoutineRow,
  RoutineRunRow,
  SessionRow,
  SignInRow,
  VaultLogin,
  SkillRow,
  TriggerEvent,
  TriggerRow,
} from './types';
import { COLOURS as colourList, GLYPHS as glyphList, normalizeDuties, normalizeSalesDuties } from './types';

const HUB_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-lab`;

const GENERIC_AGENT_ERROR = 'An error has occurred. Please try again in a few minutes.';

/** Provider billing and quota text is for us, not the person using the chat. */
export function publicAgentError(message: string | null | undefined): string {
  const text = String(message || '');
  if (/platform\.openai\.com|insufficient_quota|billing_hard_limit|usage or billing limit|exceeded your current quota|check your plan and billing|openai/i.test(text)) {
    return GENERIC_AGENT_ERROR;
  }
  return text;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Sign in to use the agent workspace.');
  return {
    Authorization: `Bearer ${token}`,
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    'Content-Type': 'application/json',
  };
}

export async function transcribeVoice(businessId: string, audio: string, mime: string, filename: string): Promise<string> {
  const payload = await hub<{ text?: string }>({ action: 'transcribe', businessId, audio, mime, filename });
  return (payload.text || '').trim();
}

export async function hub<T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T> {
  const response = await fetch(HUB_URL, { method: 'POST', headers: await authHeaders(), body: JSON.stringify(body) });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(publicAgentError(payload.error || `Agent workspace failed (${response.status})`));
  return payload as T;
}

/** Opens one watch window on a bot session and yields normalized events until it ends. */
export async function watchSession(
  sessionId: string,
  onEvent: (event: LabEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetch(HUB_URL, {
    method: 'POST',
    signal,
    headers: await authHeaders(),
    body: JSON.stringify({ action: 'watch', sessionId }),
  });
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(publicAgentError(payload.error || `Agent stream failed (${response.status})`));
  }
  if (!response.body) throw new Error('The agent stream did not start.');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const dataLine = part.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).trim()).join('');
      if (!dataLine) continue;
      try {
        onEvent(JSON.parse(dataLine) as LabEvent);
      } catch {
        // ignore malformed chunk
      }
    }
  }
}

// ── Bots ─────────────────────────────────────────────────────────────────────

async function fetchBotList(businessId: string): Promise<BotRow[]> {
  const { data, error } = await supabase.from('agent_bots').select('*')
    .eq('business_id', businessId).is('archived_at', null)
    .order('is_master', { ascending: false }).order('sort_order').order('created_at');
  if (error) throw error;
  return (data || []) as BotRow[];
}

export async function listBots(businessId: string): Promise<BotRow[]> {
  return fetchBotList(businessId);
}

// Nexus is created if this business has none. A connected website gets its web
// developer. Neither call blocks the roster: the page shows the bots it already has.
export async function provisionDefaultBots(businessId: string, bots: BotRow[]): Promise<BotRow[] | null> {
  let changed = false;
  if (!bots.some((b) => b.is_master)) {
    const { error } = await supabase.rpc('ensure_my_master_bot', { p_business_id: businessId });
    if (!error) changed = true;
  }
  if (!bots.some((b) => b.kind === 'website')) {
    const { error } = await supabase.rpc('ensure_my_website_bot', { p_business_id: businessId });
    if (!error) changed = true;
  }
  // The marketing manager comes with the Marketing app (null when it is not installed). It starts switched off.
  if (!bots.some((b) => b.kind === 'marketing')) {
    const { data, error } = await supabase.rpc('ensure_my_marketing_bot', { p_business_id: businessId });
    if (!error && data) changed = true;
  }
  // The sales manager comes with the CRM app (null when it is not installed). It starts switched off.
  if (!bots.some((b) => b.kind === 'sales')) {
    const { data, error } = await supabase.rpc('ensure_my_sales_bot', { p_business_id: businessId });
    if (!error && data) changed = true;
  }
  if (!changed) return null;
  return fetchBotList(businessId);
}

export async function createBot(businessId: string, existing: BotRow[]): Promise<BotRow> {
  const specialists = existing.filter((b) => !b.is_master && b.kind !== 'website');
  const glyph = glyphList[(specialists.length + 1) % glyphList.length];
  const colour = colourList[(specialists.length + 1) % colourList.length];
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from('agent_bots').insert({
    business_id: businessId,
    name: `Bot ${specialists.length + 1}`,
    label: 'Specialist',
    role_prompt: '',
    standing_rules: '',
    glyph,
    colour,
    sort_order: specialists.length,
    created_by: userData.user?.id || null,
  }).select('*').single();
  if (error) throw error;
  return data as BotRow;
}

export async function updateBot(botId: string, patch: Partial<Pick<BotRow, 'name' | 'label' | 'role_prompt' | 'standing_rules' | 'glyph' | 'colour' | 'model' | 'mode' | 'voice'>>): Promise<void> {
  const { error } = await supabase.from('agent_bots').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', botId);
  if (error) throw error;
}

// ── Marketing manager switch and duties ──────────────────────────────────────

export async function loadMarketingDuties(botId: string): Promise<{ paused: boolean; activatedAt: string | null; duties: Duties }> {
  const payload = await hub<{ paused?: boolean; activatedAt?: string | null; duties?: unknown }>({ action: 'marketing_duties', botId });
  return { paused: payload.paused !== false, activatedAt: payload.activatedAt || null, duties: normalizeDuties(payload.duties) };
}

/** Turns the marketing manager on or off and saves its duties; the server lines up triggers, routines, and schedules. */
export async function setMarketingDuties(botId: string, paused: boolean, duties: Duties): Promise<{ paused: boolean; duties: Duties }> {
  const payload = await hub<{ paused?: boolean; duties?: unknown }>({ action: 'set_marketing_duties', botId, paused, duties });
  return { paused: payload.paused === true, duties: normalizeDuties(payload.duties) };
}

// ── Sales manager switch and duties ──────────────────────────────────────────

export interface FirstTouchHabit {
  leads: number;
  call: number;
  text: number;
  email: number;
  usual: 'call' | 'text' | 'email' | null;
  share: number;
  sentence: string;
}

export interface PlaybookSummary {
  pipeline: string;
  stages: string[];
  /** Rules already in words. */
  rules: string[];
  notes: string;
  source: 'bot' | 'owner' | 'none';
}

export async function loadSalesDuties(botId: string): Promise<{ paused: boolean; activatedAt: string | null; duties: SalesDuties; habit: FirstTouchHabit | null; playbooks: PlaybookSummary[] }> {
  const payload = await hub<{ paused?: boolean; activatedAt?: string | null; duties?: unknown; habit?: FirstTouchHabit | null; playbooks?: PlaybookSummary[] }>({ action: 'sales_duties', botId });
  return {
    paused: payload.paused !== false,
    activatedAt: payload.activatedAt || null,
    duties: normalizeSalesDuties(payload.duties),
    habit: payload.habit || null,
    playbooks: Array.isArray(payload.playbooks) ? payload.playbooks : [],
  };
}

/** Turns the sales manager on or off and saves its duties; the server lines up triggers and the weekly review. */
export async function setSalesDuties(botId: string, paused: boolean, duties: SalesDuties): Promise<{ paused: boolean; duties: SalesDuties }> {
  const payload = await hub<{ paused?: boolean; duties?: unknown }>({ action: 'set_sales_duties', botId, paused, duties });
  return { paused: payload.paused === true, duties: normalizeSalesDuties(payload.duties) };
}

export interface PlayActionResult {
  ok?: boolean;
  status?: 'sent' | 'done' | 'skipped';
  then?: string[];
  error?: string;
  connect?: 'twilio' | 'gmail' | null;
}

/** Which lines of a staged play the owner left ticked. Omitted lines stay on. */
export interface SalesPlayParts {
  touch?: boolean;
  stage?: boolean;
  follow_up?: boolean;
  mark?: boolean;
  /** The staged quote. Unticked deletes it and still applies the rest. */
  proposal?: boolean;
  /** Create the booking. Unticked still sends the message. */
  booking?: boolean;
  /** Schedule the field quote. Unticked still sends the message. */
  field_quote?: boolean;
}

/** The owner's tap on a staged Sales play: Send delivers and applies the follow-up and stage; Done does the same after a call; Skip drops it. */
export async function actOnSalesPlay(
  playId: string,
  action: 'send' | 'done' | 'skip',
  edits: {
    body?: string;
    subject?: string;
    parts?: SalesPlayParts;
    /** People left on the booking. Send creates it with this crew. */
    bookingResourceIds?: string[];
    /** People left on the field quote. Send schedules it with this crew. */
    fieldQuoteResourceIds?: string[];
  } = {},
): Promise<PlayActionResult> {
  const result = await hub<PlayActionResult>({
    action: action === 'send' ? 'send_play' : action === 'done' ? 'done_play' : 'skip_play',
    playId,
    ...edits,
  });
  if (result.error) throw new Error(result.error);
  return result;
}

export async function archiveBot(botId: string): Promise<void> {
  const { error } = await supabase.from('agent_bots').update({ archived_at: new Date().toISOString() }).eq('id', botId);
  if (error) throw error;
}

// ── Chats ────────────────────────────────────────────────────────────────────

export async function listChats(businessId: string): Promise<ChatRow[]> {
  const { data, error } = await supabase.from('agent_chats')
    .select('*, agent_chat_members(bot_id)')
    .eq('business_id', businessId)
    .order('last_message_at', { ascending: false, nullsFirst: false });
  if (error) throw error;
  return (data || []).map((row: Record<string, unknown>) => {
    const { agent_chat_members, ...rest } = row as Record<string, unknown> & { agent_chat_members: { bot_id: string }[] };
    return { ...(rest as Omit<ChatRow, 'members'>), members: (agent_chat_members || []).map((m) => m.bot_id) };
  });
}

export async function ensureDirectChat(businessId: string, bot: BotRow, chats: ChatRow[]): Promise<ChatRow> {
  const found = chats.find((c) => c.kind === 'direct' && c.members.length === 1 && c.members[0] === bot.id);
  if (found) return found;
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from('agent_chats').insert({
    business_id: businessId,
    kind: 'direct',
    title: bot.name,
    lead_bot_id: bot.id,
    created_by: userData.user?.id || null,
  }).select('*').single();
  if (error) throw error;
  await supabase.from('agent_chat_members').insert({ chat_id: data.id, bot_id: bot.id });
  return { ...(data as Omit<ChatRow, 'members'>), members: [bot.id] };
}

export async function createGroupChat(businessId: string, title: string, botIds: string[], leadBotId: string): Promise<ChatRow> {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase.from('agent_chats').insert({
    business_id: businessId,
    kind: 'group',
    title,
    lead_bot_id: leadBotId,
    created_by: userData.user?.id || null,
  }).select('*').single();
  if (error) throw error;
  await supabase.from('agent_chat_members').insert(botIds.map((bot_id) => ({ chat_id: data.id, bot_id })));
  return { ...(data as Omit<ChatRow, 'members'>), members: botIds };
}

export interface TeamRow {
  id: string;
  business_id: string;
  title: string;
  lead_bot_id: string;
  chat_id: string;
  members: string[];
}

export async function listTeams(businessId: string): Promise<TeamRow[]> {
  const { data, error } = await supabase.from('agent_teams')
    .select('*, agent_team_members(bot_id)')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((row: Record<string, unknown>) => {
    const { agent_team_members, ...rest } = row as Record<string, unknown> & { agent_team_members: { bot_id: string }[] };
    return { ...(rest as Omit<TeamRow, 'members'>), members: (agent_team_members || []).map((m) => m.bot_id) };
  });
}

export async function createTeam(businessId: string, title: string, botIds: string[], leadBotId: string): Promise<{ team: TeamRow; chat: ChatRow }> {
  const { data: userData } = await supabase.auth.getUser();
  const { data: chat, error: chatErr } = await supabase.from('agent_chats').insert({
    business_id: businessId,
    kind: 'team',
    title,
    lead_bot_id: leadBotId,
    created_by: userData.user?.id || null,
  }).select('*').single();
  if (chatErr || !chat) throw chatErr || new Error('Could not create the team chat.');
  const { error: memErr } = await supabase.from('agent_chat_members').insert(botIds.map((bot_id) => ({ chat_id: chat.id, bot_id })));
  if (memErr) {
    await supabase.from('agent_chats').delete().eq('id', chat.id);
    throw memErr;
  }
  const { data: team, error: teamErr } = await supabase.from('agent_teams').insert({
    business_id: businessId,
    title,
    lead_bot_id: leadBotId,
    chat_id: chat.id,
  }).select('*').single();
  if (teamErr || !team) {
    await supabase.from('agent_chats').delete().eq('id', chat.id);
    throw teamErr || new Error('Could not create the team.');
  }
  const { error: tmErr } = await supabase.from('agent_team_members').insert(botIds.map((bot_id) => ({ team_id: team.id, bot_id })));
  if (tmErr) {
    await supabase.from('agent_chats').delete().eq('id', chat.id);
    throw new Error(tmErr.code === '23505' ? 'One of those bots is already on another team.' : tmErr.message);
  }
  return {
    team: { ...(team as Omit<TeamRow, 'members'>), members: botIds },
    chat: { ...(chat as Omit<ChatRow, 'members'>), members: botIds },
  };
}

export async function updateTeam(teamId: string, title: string, botIds: string[], leadBotId: string): Promise<void> {
  const { data: team, error } = await supabase.from('agent_teams').select('id, chat_id').eq('id', teamId).single();
  if (error || !team) throw error || new Error('Team not found.');
  const { error: titleErr } = await supabase.from('agent_teams').update({
    title,
    lead_bot_id: leadBotId,
    updated_at: new Date().toISOString(),
  }).eq('id', teamId);
  if (titleErr) throw titleErr;
  const { error: chatErr } = await supabase.from('agent_chats').update({ title, lead_bot_id: leadBotId }).eq('id', team.chat_id);
  if (chatErr) throw chatErr;

  const { data: current } = await supabase.from('agent_team_members').select('bot_id').eq('team_id', teamId);
  const currentIds = new Set((current || []).map((row) => row.bot_id as string));
  const next = new Set(botIds);
  const remove = [...currentIds].filter((id) => !next.has(id));
  const add = botIds.filter((id) => !currentIds.has(id));
  if (add.length) {
    const { error: addTeam } = await supabase.from('agent_team_members').insert(add.map((bot_id) => ({ team_id: teamId, bot_id })));
    if (addTeam) throw new Error(addTeam.code === '23505' ? 'One of those bots is already on another team.' : addTeam.message);
    const { error: addChat } = await supabase.from('agent_chat_members').insert(add.map((bot_id) => ({ chat_id: team.chat_id, bot_id })));
    if (addChat) throw addChat;
  }
  if (remove.length) {
    const { error: dropTeam } = await supabase.from('agent_team_members').delete().eq('team_id', teamId).in('bot_id', remove);
    if (dropTeam) throw dropTeam;
    const { error: dropChat } = await supabase.from('agent_chat_members').delete().eq('chat_id', team.chat_id).in('bot_id', remove);
    if (dropChat) throw dropChat;
  }
}

export async function deleteChat(chatId: string): Promise<void> {
  const { error } = await supabase.from('agent_chats').delete().eq('id', chatId);
  if (error) throw error;
}

// ── Messages, sessions ───────────────────────────────────────────────────────

export interface ChatPreview { text: string; author: MessageRow['author']; at: string }

/** One-line preview per chat for the roster: the newest text, image or file message. */
export function previewOf(m: Pick<MessageRow, 'kind' | 'content' | 'author' | 'created_at' | 'attachments'> & { meta?: MessageRow['meta'] }): ChatPreview {
  if (m.meta?.source === 'voice') return { text: 'Voice conversation', author: m.author, at: m.created_at };
  let text = m.content || '';
  if (m.kind === 'image') text = text || 'Sent an image';
  else if (m.kind === 'file') text = text || `Sent ${m.attachments?.[0]?.name || 'a file'}`;
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[*_`#>]/g, '').replace(/\s+/g, ' ').trim();
  return { text, author: m.author, at: m.created_at };
}

export async function listChatPreviews(businessId: string): Promise<Record<string, ChatPreview>> {
  const { data, error } = await supabase.from('agent_messages')
    .select('chat_id, kind, content, author, created_at, attachments, meta')
    .eq('business_id', businessId)
    .in('kind', ['text', 'image', 'file'])
    .order('created_at', { ascending: false })
    .limit(400);
  if (error) throw error;
  const out: Record<string, ChatPreview> = {};
  for (const row of (data || []) as (Pick<MessageRow, 'chat_id' | 'kind' | 'content' | 'author' | 'created_at' | 'attachments' | 'meta'>)[]) {
    if (!out[row.chat_id]) out[row.chat_id] = previewOf(row);
  }
  return out;
}

export async function listMessages(chatId: string): Promise<MessageRow[]> {
  // Newest window, then chronological. Ascending + limit returns the oldest
  // rows, so a long chat reloaded without the message that was just sent.
  const { data, error } = await supabase.from('agent_messages').select('*')
    .eq('chat_id', chatId).order('created_at', { ascending: false }).limit(500);
  if (error) throw error;
  return ((data || []) as MessageRow[]).reverse();
}

export async function listSessions(businessId: string): Promise<SessionRow[]> {
  const { data, error } = await supabase.from('agent_bot_sessions').select('*')
    .eq('business_id', businessId).neq('status', 'expired');
  if (error) throw error;
  return (data || []) as SessionRow[];
}

// ── Skills, routines, artifacts ──────────────────────────────────────────────

export async function listSkills(botId: string): Promise<SkillRow[]> {
  const { data } = await supabase.from('agent_skills').select('*').eq('bot_id', botId).order('created_at');
  return (data || []) as SkillRow[];
}

export async function saveSkill(businessId: string, botId: string, skill: { id?: string; name: string; instructions: string }): Promise<void> {
  if (skill.id) {
    await supabase.from('agent_skills').update({ name: skill.name, instructions: skill.instructions, updated_at: new Date().toISOString() }).eq('id', skill.id);
  } else {
    await supabase.from('agent_skills').insert({ business_id: businessId, bot_id: botId, name: skill.name, instructions: skill.instructions });
  }
}

export async function deleteSkill(skillId: string): Promise<void> {
  await supabase.from('agent_skills').delete().eq('id', skillId);
}

export async function listRoutines(botId: string): Promise<RoutineRow[]> {
  const { data } = await supabase.from('agent_routines').select('*').eq('bot_id', botId).order('created_at');
  return (data || []) as RoutineRow[];
}

export async function listRoutineRuns(routineId: string): Promise<RoutineRunRow[]> {
  const { data } = await supabase.from('agent_routine_runs').select('*').eq('routine_id', routineId).order('started_at', { ascending: false }).limit(20);
  return (data || []) as RoutineRunRow[];
}

export async function upsertRoutine(businessId: string, botId: string, routine: Partial<RoutineRow> & { name: string; prompt: string }): Promise<void> {
  const row = {
    business_id: businessId,
    bot_id: botId,
    name: routine.name,
    prompt: routine.prompt,
    frequency: routine.frequency || 'daily',
    time_of_day: routine.time_of_day || '09:00',
    day_of_week: routine.day_of_week ?? null,
    timezone: routine.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Vancouver',
    enabled: routine.enabled ?? true,
  };
  const query = routine.id
    ? supabase.from('agent_routines').update(row).eq('id', routine.id)
    : supabase.from('agent_routines').insert(row);
  const { error } = await query;
  if (error) throw error;
}

export async function setRoutineEnabled(routineId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.from('agent_routines').update({ enabled }).eq('id', routineId);
  if (error) throw error;
  const { data } = await supabase.from('agent_messages').select('id, meta').eq('kind', 'routine').filter('meta->>routine_id', 'eq', routineId);
  for (const row of (data || []) as { id: string; meta: Record<string, unknown> | null }[]) {
    const meta = row.meta || {};
    if (meta.status === 'running') continue;
    await supabase.from('agent_messages').update({ meta: { ...meta, status: enabled ? 'enabled' : 'off' } }).eq('id', row.id);
  }
}

export async function deleteRoutine(routineId: string): Promise<void> {
  await supabase.from('agent_routines').delete().eq('id', routineId);
}

export async function listArtifacts(filter: { botId?: string; chatId?: string }): Promise<ArtifactRow[]> {
  let query = supabase.from('agent_artifacts').select('*').not('storage_path', 'like', 'hidden/%').order('created_at', { ascending: false }).limit(100);
  if (filter.botId) query = query.eq('bot_id', filter.botId);
  if (filter.chatId) query = query.eq('chat_id', filter.chatId);
  const { data } = await query;
  return (data || []) as ArtifactRow[];
}

// ── Memory ───────────────────────────────────────────────────────────────────

/** Shared memories plus the bot's own, newest first. */
export async function listMemories(businessId: string, botId: string | null): Promise<MemoryRow[]> {
  let query = supabase.from('agent_memories').select('*').eq('business_id', businessId).order('created_at', { ascending: false }).limit(300);
  query = botId ? query.or(`bot_id.is.null,bot_id.eq.${botId}`) : query.is('bot_id', null);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as MemoryRow[];
}

export async function saveMemory(businessId: string, memory: { id?: string; bot_id?: string | null; kind: MemoryKind; content: string }): Promise<void> {
  const content = memory.content.trim().slice(0, 600);
  if (!content) return;
  const { error } = memory.id
    ? await supabase.from('agent_memories').update({ kind: memory.kind, content, updated_at: new Date().toISOString() }).eq('id', memory.id)
    : await supabase.from('agent_memories').insert({ business_id: businessId, bot_id: memory.bot_id ?? null, kind: memory.kind, content, source: 'owner' });
  if (error) throw error;
}

export async function deleteMemory(memoryId: string): Promise<void> {
  const { error } = await supabase.from('agent_memories').delete().eq('id', memoryId);
  if (error) throw error;
}

// ── Triggers ─────────────────────────────────────────────────────────────────

export async function listTriggers(botId: string): Promise<TriggerRow[]> {
  const { data } = await supabase.from('agent_triggers').select('*').eq('bot_id', botId).order('created_at');
  return (data || []) as TriggerRow[];
}

export async function upsertTrigger(businessId: string, botId: string, trigger: { id?: string; event: TriggerEvent; prompt: string; enabled?: boolean }): Promise<void> {
  const row = { business_id: businessId, bot_id: botId, event: trigger.event, prompt: trigger.prompt.trim(), enabled: trigger.enabled ?? true, updated_at: new Date().toISOString() };
  const { error } = trigger.id
    ? await supabase.from('agent_triggers').update(row).eq('id', trigger.id)
    : await supabase.from('agent_triggers').upsert({ ...row, last_checked_at: new Date().toISOString() }, { onConflict: 'bot_id,event' });
  if (error) throw error;
}

export async function setTriggerEnabled(triggerId: string, enabled: boolean): Promise<void> {
  const patch: Record<string, unknown> = { enabled, updated_at: new Date().toISOString() };
  if (enabled) patch.last_checked_at = new Date().toISOString();
  const { error } = await supabase.from('agent_triggers').update(patch).eq('id', triggerId);
  if (error) throw error;
}

export async function deleteTrigger(triggerId: string): Promise<void> {
  const { error } = await supabase.from('agent_triggers').delete().eq('id', triggerId);
  if (error) throw error;
}

// ── Standing approvals ───────────────────────────────────────────────────────

export async function listApprovalRules(botId: string): Promise<ApprovalRuleRow[]> {
  const { data } = await supabase.from('agent_approval_rules').select('*').eq('bot_id', botId).order('created_at');
  return (data || []) as ApprovalRuleRow[];
}

export async function deleteApprovalRule(ruleId: string): Promise<void> {
  const { error } = await supabase.from('agent_approval_rules').delete().eq('id', ruleId);
  if (error) throw error;
}

// ── Settings ─────────────────────────────────────────────────────────────────

export async function loadAgentSettings(businessId: string): Promise<AgentSettingsRow> {
  const { data } = await supabase.from('agent_settings').select('*').eq('business_id', businessId).maybeSingle();
  const row = (data || {}) as Partial<AgentSettingsRow>;
  return {
    business_id: businessId,
    notify_level: row.notify_level === 'off' || row.notify_level === 'all' ? row.notify_level : 'important',
    auto_memory: row.auto_memory !== false,
  };
}

export async function saveAgentSettings(businessId: string, patch: { notify_level?: NotifyLevel; auto_memory?: boolean }): Promise<void> {
  const { error } = await supabase.from('agent_settings').upsert({ business_id: businessId, ...patch, updated_at: new Date().toISOString() }, { onConflict: 'business_id' });
  if (error) throw error;
}

// ── Activity ─────────────────────────────────────────────────────────────────

function humanTool(name: string): string {
  const words = name.replace(/^propose_/, 'proposed ').replace(/^list_/, 'looked at ').replace(/^read_/, 'read ').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

type ActionRow = { id: string; name: string | null; type: string; status: string; result: Record<string, unknown> | null; payload: Record<string, unknown> | null; created_at: string; session: { bot_id: string; chat_id: string } | null };
type ApprovalRow = { id: string; bot_id: string | null; chat_id: string; content: string; meta: Record<string, unknown>; updated_at: string };
type RunRow = { id: string; status: string; summary: string | null; started_at: string; routine: { name: string; bot_id: string; chat_id: string | null } | null };
type NoticeRow = { id: string; bot_id: string | null; chat_id: string; kind: string; title: string; body: string; created_at: string };

/** Everything the bots did for this business, newest first, from the tables that already record it. */
/** Saved website logins. The password column is not readable from the app. */
export async function listLoginVault(businessId: string): Promise<VaultLogin[]> {
  const { data, error } = await supabase.from('agent_login_vault')
    .select('id, host, account_label, updated_at')
    .eq('business_id', businessId)
    .order('host');
  if (error) { console.warn('[agents] vault query failed:', error.message); return []; }
  return (data || []) as VaultLogin[];
}

export async function removeLoginVault(id: string): Promise<void> {
  const { error } = await supabase.from('agent_login_vault').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Sites this bot's current browser has been signed in to by the owner. Origins only; the password is not in this list. */
export async function listBotSignIns(botId: string): Promise<SignInRow[]> {
  const { data, error } = await supabase.from('agent_actions')
    .select('id, name, payload, created_at, session:agent_bot_sessions!inner(id, bot_id, status)')
    .eq('type', 'browser_authentication')
    .eq('session.bot_id', botId)
    .neq('session.status', 'expired')
    .contains('result', { decision: 'submit' })
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) { console.warn('[agents] sign-ins query failed:', error.message); return []; }
  const seen = new Map<string, SignInRow>();
  for (const row of (data || []) as unknown as { id: string; name: string; payload: { host?: string; origin?: string }; created_at: string; session: { id: string } }[]) {
    const host = String(row.payload?.host || row.name || '').replace(/^https?:\/\//, '').replace(/^www\./, '');
    if (!host || seen.has(host)) continue;
    seen.set(host, { id: row.id, host, at: row.created_at, session_id: row.session.id });
  }
  return [...seen.values()];
}

/** Sign the bot's browser out of everything: its next message starts on a fresh computer. */
export async function signOutBotBrowser(botId: string): Promise<void> {
  const { error } = await supabase.from('agent_bot_sessions')
    .update({ status: 'expired', activity: 'Signed out', updated_at: new Date().toISOString() })
    .eq('bot_id', botId)
    .in('status', ['idle', 'done', 'blocked']);
  if (error) throw new Error(error.message);
}

export async function listActivity(businessId: string, limit = 120): Promise<ActivityItem[]> {
  const since = new Date(Date.now() - 14 * 86_400_000).toISOString();
  const [actions, approvals, runs, notices] = await Promise.all([
    supabase.from('agent_actions').select('id, name, type, status, result, payload, created_at, session:agent_bot_sessions(bot_id, chat_id)')
      .eq('business_id', businessId).eq('type', 'function_call').gte('created_at', since).order('created_at', { ascending: false }).limit(limit),
    supabase.from('agent_messages').select('id, bot_id, chat_id, content, meta, updated_at')
      .eq('business_id', businessId).eq('kind', 'approval').gte('updated_at', since).order('updated_at', { ascending: false }).limit(60),
    supabase.from('agent_routine_runs').select('id, status, summary, started_at, routine:agent_routines(name, bot_id, chat_id)')
      .eq('business_id', businessId).gte('started_at', since).order('started_at', { ascending: false }).limit(40),
    supabase.from('agent_notices').select('id, bot_id, chat_id, kind, title, body, created_at')
      .eq('business_id', businessId).gte('created_at', since).order('created_at', { ascending: false }).limit(60),
  ]);
  for (const [label, res] of [['actions', actions], ['approvals', approvals], ['runs', runs], ['notices', notices]] as const) {
    if (res.error) console.warn(`[agents] activity ${label} query failed:`, res.error.message);
  }
  const items: ActivityItem[] = [];
  for (const row of ((actions.data || []) as unknown as ActionRow[])) {
    const err = row.result && typeof row.result.error === 'string' ? String(row.result.error) : null;
    const name = row.name || 'tool';
    if (/^(recall|list_teammates|lookup_help|follow_ups_card|routine_status)$/.test(name) && !err) continue;
    items.push({
      id: `a:${row.id}`, at: row.created_at, bot_id: row.session?.bot_id ?? null, chat_id: row.session?.chat_id ?? null,
      kind: 'tool', title: humanTool(name), detail: err ? err.slice(0, 200) : (row.result?.retried ? 'Worked on the second try' : null),
      state: err ? 'failed' : row.status === 'pending' ? 'running' : 'ok',
    });
  }
  for (const row of ((approvals.data || []) as unknown as ApprovalRow[])) {
    const status = String(row.meta.status || 'pending');
    const title = String(row.meta.card_title || row.meta.title || row.content.split('\n')[0] || 'Approval').slice(0, 120);
    const state: ActivityItem['state'] = ['approved', 'sent', 'enabled', 'synced', 'published', 'submitted'].includes(status) ? 'ok'
      : ['denied', 'discarded', 'dismissed', 'declined', 'cancelled', 'expired'].includes(status) ? 'denied'
        : status === 'failed' ? 'failed' : 'waiting';
    items.push({
      id: `p:${row.id}`, at: row.updated_at, bot_id: row.bot_id, chat_id: row.chat_id, kind: 'approval', title,
      detail: status === 'submitted' ? 'You signed in' : status === 'cancelled' ? 'Sign-in cancelled' : status === 'expired' ? 'Sign-in expired'
        : state === 'ok' ? (row.meta.always ? 'Approved · always allowed from now on' : 'Approved') : state === 'denied' ? 'Declined' : state === 'failed' ? String(row.meta.error || 'Failed') : 'Waiting for you',
      state,
    });
  }
  for (const row of ((runs.data || []) as unknown as RunRow[])) {
    items.push({
      id: `r:${row.id}`, at: row.started_at, bot_id: row.routine?.bot_id ?? null, chat_id: row.routine?.chat_id ?? null, kind: 'routine',
      title: `Routine: ${row.routine?.name || 'routine'}`, detail: row.summary ? row.summary.slice(0, 200) : null,
      state: row.status === 'failed' ? 'failed' : row.status === 'running' ? 'running' : 'ok',
    });
  }
  for (const row of ((notices.data || []) as unknown as NoticeRow[])) {
    items.push({
      id: `n:${row.id}`, at: row.created_at, bot_id: row.bot_id, chat_id: row.chat_id, kind: 'notice', title: row.title, detail: row.body || null,
      state: row.kind === 'failed' ? 'failed' : row.kind === 'waiting' ? 'waiting' : 'ok',
    });
  }
  items.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
  return items.slice(0, limit);
}

const signedUrlCache = new Map<string, { url: string; expires: number }>();
export async function signedUrl(path: string): Promise<string | null> {
  const cached = signedUrlCache.get(path);
  if (cached && cached.expires > Date.now()) return cached.url;
  const { data } = await supabase.storage.from('agent-artifacts').createSignedUrl(path, 3600);
  if (!data?.signedUrl) return null;
  signedUrlCache.set(path, { url: data.signedUrl, expires: Date.now() + 50 * 60 * 1000 });
  return data.signedUrl;
}

/** Which marketing channels this business has connected: instagram, facebook, linkedin, google. */
export async function loadConnectedPlatforms(businessId: string): Promise<Set<DutyGroup>> {
  const [{ data: social }, { data: gbp }] = await Promise.all([
    supabase
      .from('social_media_connections')
      .select('platform')
      .eq('business_id', businessId)
      .eq('is_active', true)
      .in('platform', ['facebook', 'instagram', 'linkedin']),
    supabase
      .from('google_business_connections')
      .select('id')
      .eq('business_id', businessId)
      .in('provider', ['ayrshare', 'google'])
      .eq('is_active', true)
      .limit(1),
  ]);
  const out = new Set<DutyGroup>();
  for (const row of social || []) {
    const p = String((row as { platform: string }).platform);
    if (p === 'facebook' || p === 'instagram' || p === 'linkedin') out.add(p);
  }
  if ((gbp || []).length > 0) out.add('google');
  return out;
}
